/**
 * Error Stack Parser
 *
 * Parses error stack traces to extract source file locations
 * Works with both development and production builds (with source maps)
 */

export interface StackFrame {
  file: string
  line: number | null
  column: number | null
  functionName: string | null
  source: string
}

/**
 * Parse error stack trace into structured frames
 */
export function parseErrorStack(error: Error): StackFrame[] {
  if (!error.stack) {
    return []
  }

  const frames: StackFrame[] = []
  const lines = error.stack.split('\n')

  for (const line of lines) {
    const frame = parseStackLine(line)
    if (frame) {
      frames.push(frame)
    }
  }

  return frames
}

/**
 * Parse a single stack trace line
 * Supports formats:
 * - Chrome/Edge: "at functionName (file:///path/to/file.tsx:123:45)"
 * - Firefox: "functionName@file:///path/to/file.tsx:123:45"
 * - Next.js webpack: "at functionName (webpack-internal:///./path/to/file.tsx:123:45)"
 * - Next.js production: "at functionName (https://domain.com/_next/static/chunks/file.js:123:45)"
 */
function parseStackLine(line: string): StackFrame | null {
  // Skip empty lines and error message lines
  if (
    !line.trim() ||
    line.startsWith('Error:') ||
    line.startsWith('TypeError:')
  ) {
    return null
  }

  // Match Chrome/Edge format: "at functionName (file:///path/to/file.tsx:123:45)"
  const chromeMatch = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/)
  if (chromeMatch) {
    return {
      functionName: chromeMatch[1] || null,
      file: chromeMatch[2],
      line: parseInt(chromeMatch[3], 10),
      column: parseInt(chromeMatch[4], 10),
      source: line,
    }
  }

  // Match Firefox format: "functionName@file:///path/to/file.tsx:123:45"
  const firefoxMatch = line.match(/(.+?)@(.+?):(\d+):(\d+)/)
  if (firefoxMatch) {
    return {
      functionName: firefoxMatch[1] || null,
      file: firefoxMatch[2],
      line: parseInt(firefoxMatch[3], 10),
      column: parseInt(firefoxMatch[4], 10),
      source: line,
    }
  }

  // Match simplified format: "at file:///path/to/file.tsx:123:45"
  const simpleMatch = line.match(/at\s+(.+?):(\d+):(\d+)/)
  if (simpleMatch) {
    return {
      functionName: null,
      file: simpleMatch[1],
      line: parseInt(simpleMatch[2], 10),
      column: parseInt(simpleMatch[3], 10),
      source: line,
    }
  }

  return null
}

/**
 * Extract source file path from stack frame
 * Normalizes webpack paths and Next.js paths to actual source paths
 */
export function extractSourcePath(frame: StackFrame): string | null {
  if (!frame.file) {
    return null
  }

  let path = frame.file

  // Remove protocol prefix
  path = path.replace(/^file:\/\//, '')
  path = path.replace(/^https?:\/\//, '')

  // Extract from webpack-internal paths
  // webpack-internal:///./apps/web/components/file.tsx -> apps/web/components/file.tsx
  if (path.includes('webpack-internal://')) {
    const match = path.match(/webpack-internal:\/\/\/\.\/(.+)/)
    if (match) {
      path = match[1]
    }
  }

  // Extract from Next.js chunk paths
  // https://domain.com/_next/static/chunks/app/page-abc123.js -> app/page.tsx (approximate)
  if (path.includes('/_next/static/chunks/')) {
    const match = path.match(
      /\/_next\/static\/chunks\/(.+?)(?:-[a-f0-9]+)?\.js/,
    )
    if (match) {
      // Try to map chunk name back to source file
      // This is approximate - actual mapping requires source map
      path = match[1]
    }
  }

  // Remove query strings and hashes
  path = path.split('?')[0]
  path = path.split('#')[0]

  return path
}

/**
 * Format stack frame for display
 */
export function formatStackFrame(frame: StackFrame): string {
  const sourcePath = extractSourcePath(frame)
  const location = sourcePath || frame.file

  if (frame.line !== null && frame.column !== null) {
    if (frame.functionName) {
      return `${frame.functionName} (${location}:${frame.line}:${frame.column})`
    }
    return `${location}:${frame.line}:${frame.column}`
  }

  if (frame.functionName) {
    return `${frame.functionName} (${location})`
  }

  return location || frame.source
}

/**
 * Find relevant stack frames (filter out node_modules, Next.js internals)
 */
export function getRelevantFrames(frames: StackFrame[]): StackFrame[] {
  return frames.filter(frame => {
    const path = extractSourcePath(frame) || frame.file

    // Skip node_modules
    if (path.includes('node_modules/')) {
      return false
    }

    // Skip Next.js internals (but keep our app code)
    if (path.includes('/_next/') && !path.includes('/apps/web/')) {
      return false
    }

    // Skip webpack runtime
    if (path.includes('webpack/runtime/')) {
      return false
    }

    return true
  })
}

/**
 * Get the most relevant error location (first relevant frame)
 */
export function getErrorLocation(error: Error): {
  file: string | null
  line: number | null
  column: number | null
  functionName: string | null
} {
  const frames = parseErrorStack(error)
  const relevantFrames = getRelevantFrames(frames)

  if (relevantFrames.length === 0) {
    return {
      file: null,
      line: null,
      column: null,
      functionName: null,
    }
  }

  const firstFrame = relevantFrames[0]
  return {
    file: extractSourcePath(firstFrame),
    line: firstFrame.line,
    column: firstFrame.column,
    functionName: firstFrame.functionName,
  }
}
